import {
  ApprovalWorkflowEditor,
  ApproverList,
  SaveAndCancel,
  StickyActionsHeader,
} from "@/components";
import HistoryLogTable from "@/components/History/HistoryLogTable";
import { PageWrapper } from "@/components/PageWrapper";
import { CONTENT_WIDTH, GUTTER_SIZE } from "@/enums";
import { routes } from "@/helpers";
import { Card, Col, Form, Row } from "antd";
import _ from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import {
  useGetAllNotificationGroupsQuery,
  useGetApprovalWorkflowQuery,
  useGetHistoryLogsByIdQuery,
  useGetProjectRolesQuery,
  useSaveApprovalWorkflowMutation,
} from "@/services";
import { useProjectTypes } from "../hooks";

const EditApprovalWorkflow = () => {
  const intl = useIntl();
  const { id } = useParams();
  const navigate = useNavigate();
  const projectTypes = useProjectTypes();

  const [isValidForm, setIsValidForm] = useState(false);
  const [editedApprovalWorkflow, setEditedApprovalWorkflow] = useState();

  const {
    data: savedApprovalWorkflow,
    isError,
    isLoading,
  } = useGetApprovalWorkflowQuery(id);
  const [saveApprovalWorkflow] = useSaveApprovalWorkflowMutation();
  const { data: notificationGroups } = useGetAllNotificationGroupsQuery();
  const { data: projectRoles } = useGetProjectRolesQuery();
  const { refetch: refetchHistory } = useGetHistoryLogsByIdQuery({
    parentEntityId: id,
  });

  useEffect(() => {
    setEditedApprovalWorkflow(savedApprovalWorkflow);
  }, [savedApprovalWorkflow]);

  useEffect(() => {
    if (!isError) return;

    toast.error(
      intl.formatMessage({
        id: "projectRoles.getProjectRolesErrorMessage",
      })
    );
  }, [isError, intl]);

  const handleSaveButtonClick = () => {
    saveApprovalWorkflow(editedApprovalWorkflow)
      .unwrap()
      .then(() => {
        refetchHistory();
        toast.success(intl.formatMessage({ id: "general.toastChangesSaved" }));
      })
      .catch(() =>
        toast.error(
          intl.formatMessage({ id: "general.toastSomethingWentWrong" })
        )
      );
  };

  const labelsDict = useMemo(() => {
    const notificationGroupsDict = _.mapValues(
      _.keyBy(notificationGroups, "id"),
      "name"
    );
    const roleGroupsDict = _.mapValues(_.keyBy(projectRoles, "id"), "name");

    return { ...notificationGroupsDict, ...roleGroupsDict };
  }, [notificationGroups, projectRoles]);

  return (
    <PageWrapper
      pageTitle={intl.formatMessage({ id: "approvalWorkFlows.editTitle" })}
      backButtonRoute={routes.admin.approvalWorkFlows.list}
      pageActions={
        <StickyActionsHeader
          rightChildren={
            <SaveAndCancel
              pageName="edit-approval-workflow"
              saveOnClick={handleSaveButtonClick}
              cancelOnClick={() =>
                navigate(routes.admin.approvalWorkFlows.list)
              }
              saveDisabled={!isValidForm}
            />
          }
        />
      }
    >
      <Row gutter={GUTTER_SIZE.large}>
        <Col span={CONTENT_WIDTH.half}>
          <Card>
            <Form layout="vertical">
              <ApprovalWorkflowEditor
                projectTypes={projectTypes}
                onSetApprovalWorkFlow={setEditedApprovalWorkflow}
                approvalWorkflow={editedApprovalWorkflow}
                setIsValidForm={setIsValidForm}
              />
              <>
                {editedApprovalWorkflow?.id && (
                  <ApproverList approvalWorkflow={editedApprovalWorkflow} />
                )}
              </>
            </Form>
          </Card>
        </Col>
        <Col span={CONTENT_WIDTH.half}>
          <Card loading={isLoading}>
            <HistoryLogTable
              parentEntityId={id}
              additionalLabels={labelsDict}
            />
          </Card>
        </Col>
      </Row>
    </PageWrapper>
  );
};

export default EditApprovalWorkflow;
